import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Inbox, Link2, Search, Download, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import EmailViewerDialog from "@/components/EmailViewerDialog";
import { toast } from "sonner";

const FILTERS = [
  { id: "unread", label: "Unread" },
  { id: "all", label: "All" },
  { id: "unmatched", label: "Unmatched" },
];

export default function EmailInbox() {
  const [filter, setFilter] = useState("unread");
  const [direction, setDirection] = useState("");  // "" | inbound | outbound
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ filter, limit: "100" });
      if (direction) params.set("direction", direction);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/emails/inbox?${params}`);
      setRows(data?.items || []);
    } catch (e) {
      toast.error("Failed to load emails");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, direction]);

  const backfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/emails/inbound/backfill");
      toast.success(`Backfill: pulled ${data.pulled}, saved ${data.created}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally { setBackfilling(false); }
  };

  const counts = useMemo(() => ({
    unmatched: rows.filter((r) => r.is_unmatched).length,
    unread: rows.filter((r) => !r.read_at && r.direction === "inbound").length,
  }), [rows]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center">
            <Inbox className="w-5 h-5 text-sky-600"/>
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold">Email inbox</h1>
            <p className="text-sm text-zinc-500">Every inbound and outbound email LeadForge touched. Assign unmatched replies to the right customer.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1"/>Refresh</Button>
          <Button onClick={backfill} disabled={backfilling} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="email-inbox-backfill-btn">
            <Download className="w-3.5 h-3.5 mr-1"/>{backfilling ? "Backfilling…" : "Backfill from Resend"}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid="email-inbox-filter">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-xs ${filter === f.id ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"} ${f.id !== "unread" ? "border-l border-zinc-200" : ""}`}
              data-testid={`email-inbox-filter-${f.id}`}
            >{f.label}</button>
          ))}
        </div>
        <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden">
          {["", "inbound", "outbound"].map((d, i) => (
            <button
              key={d || "any"}
              onClick={() => setDirection(d)}
              className={`px-3 py-1.5 text-xs ${direction === d ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"} ${i > 0 ? "border-l border-zinc-200" : ""}`}
            >{d || "Any"}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, body, email…" className="h-8 w-72"/>
          <Button type="submit" size="sm" variant="outline"><Search className="w-3.5 h-3.5"/></Button>
        </form>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden" data-testid="email-inbox-list">
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <Mail className="w-8 h-8 text-zinc-300 mx-auto mb-2"/>
            <div className="text-sm text-zinc-500">No emails match this filter.</div>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {rows.map((r) => {
              const isInbound = r.direction === "inbound";
              const unread = !r.read_at && isInbound;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setOpenId(r.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-sky-50/40 transition-colors ${unread ? "bg-amber-50/40" : ""}`}
                    data-testid={`email-inbox-row-${r.id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] font-mono-accent uppercase tracking-widest px-1.5 py-0.5 rounded ${isInbound ? "bg-sky-100 text-sky-800" : "bg-zinc-100 text-zinc-600"}`}>
                        {isInbound ? "IN" : "OUT"}
                      </span>
                      {r.is_unmatched && (
                        <span className="text-[9px] font-mono-accent uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Unmatched</span>
                      )}
                      {unread && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Unread"/>}
                      <span className="text-sm font-medium text-zinc-900 truncate">{r.subject || "(no subject)"}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="font-mono-accent">
                        {isInbound ? r.from_email : r.to_email} {r.customer_name && `· ${r.customer_name}`}
                      </span>
                      <span>·</span>
                      <span>{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
                    </div>
                    <div className="text-xs text-zinc-600 mt-1 line-clamp-1">{r.body || ""}</div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <EmailViewerDialog
        emailId={openId}
        open={!!openId}
        onClose={() => setOpenId(null)}
        onChanged={load}
      />
    </div>
  );
}
